import React, { useEffect, useMemo, useState } from "react";
import { Col, Row, Card, Table, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import "../styles/detailRes.css";
import { determinePersonality } from "../../client/utils/personalityUtils";
import {
  buildDiscGraphDebugRows,
  calculateDiscGraphs,
  getDiscRawScores,
} from "../../shared/discGraphUtils";
import {
  DISC_NORM_TABLE,
  DISC_TRAITS,
} from "../../shared/discMappingTables";

const DISC_GRAPH_SCALE = { min: -8, max: 8, stepSize: 2 };
const DISC_AXIS_TICKS = [8, 6, 4, 2, 0, -2, -4, -6, -8];
const DISC_NORM_LEVELS = [8, 6, 4, 2, 0, -2, -4, -6];
const DISC_TOP_LABELS = ["A", "B", "C", "D"];

const DISC_GRAPH_VIEWBOX = {
  width: 280,
  height: 620,
  left: 34,
  right: 224,
  top: 86,
  bottom: 524,
};

const DISC_RAW_NUMBER_LAYOUT = {
  fontSize: 7.9,
  rowHeight: 8.4,
};

const getNormRawValues = (normTable, trait, level) =>
  normTable?.[trait]?.[level] ?? normTable?.[trait]?.[String(level)] ?? [];

const clampChartValue = (value) =>
  Math.max(
    DISC_GRAPH_SCALE.min,
    Math.min(DISC_GRAPH_SCALE.max, Number(value) || 0)
  );

const DiscNormGraph = ({
  title,
  subtitle,
  normTable,
  rawScores,
  chartValues,
  accentColor,
}) => {
  const { width, height, left, right, top, bottom } = DISC_GRAPH_VIEWBOX;
  const plotHeight = bottom - top;
  const columnWidth = (right - left) / DISC_TRAITS.length;

  const getX = (trait) =>
    left + (DISC_TRAITS.indexOf(trait) + 0.5) * columnWidth;

  const getY = (value) => {
    const clampedValue = clampChartValue(value);
    return (
      top +
      ((DISC_GRAPH_SCALE.max - clampedValue) /
        (DISC_GRAPH_SCALE.max - DISC_GRAPH_SCALE.min)) *
        plotHeight
    );
  };
  const getCellY = (value) =>
    (getY(value) + getY(value - DISC_GRAPH_SCALE.stepSize)) / 2;

  const getRawNumberY = (trait, rawValue) => {
    for (const level of DISC_NORM_LEVELS) {
      const rawValues = getNormRawValues(normTable, trait, level);
      const rawIndex = rawValues.findIndex(
        (candidate) => String(candidate) === String(rawValue)
      );

      if (rawIndex >= 0) {
        const cellY = getCellY(level);
        const { rowHeight } = DISC_RAW_NUMBER_LAYOUT;
        const startY = cellY - ((rawValues.length - 1) * rowHeight) / 2;
        return startY + rawIndex * rowHeight;
      }
    }

    const clampedValue = clampChartValue(chartValues?.[trait]);
    const fallbackLevel = Math.max(
      DISC_GRAPH_SCALE.min + DISC_GRAPH_SCALE.stepSize,
      Math.min(
        DISC_GRAPH_SCALE.max,
        Math.ceil(clampedValue / DISC_GRAPH_SCALE.stepSize) *
          DISC_GRAPH_SCALE.stepSize
      )
    );
    return getCellY(fallbackLevel);
  };

  const points = DISC_TRAITS.map((trait) => ({
    trait,
    x: getX(trait),
    y: getRawNumberY(trait, rawScores?.[trait] ?? 0),
    rawValue: rawScores?.[trait] ?? 0,
    chartValue: chartValues?.[trait] ?? 0,
  }));

  const pointPath = points.map((point) => `${point.x},${point.y}`).join(" ");

  return (
    <div className="disc-norm-graph" style={{ "--disc-accent": accentColor }}>
      <svg
        className="disc-norm-graph-svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${title} ${subtitle}`}
      >
        <text className="disc-norm-title" x="34" y="28">
          {title}
        </text>
        <text className="disc-norm-subtitle" x="34" y="50">
          {subtitle}
        </text>

        {DISC_TOP_LABELS.map((label, index) => (
          <text
            key={`top-${label}`}
            className="disc-norm-top-label"
            x={left + (index + 0.5) * columnWidth}
            y={top - 18}
          >
            {label}
          </text>
        ))}

        {[0, 1, 2, 3, 4].map((index) => {
          const x = left + index * columnWidth;
          return (
            <line
              key={`vertical-${index}`}
              className={index === 0 || index === 4 ? "disc-norm-border" : "disc-norm-grid"}
              x1={x}
              y1={top}
              x2={x}
              y2={bottom}
            />
          );
        })}

        {DISC_AXIS_TICKS.map((tick) => {
          const y = getY(tick);
          return (
            <g key={`tick-${tick}`}>
              <line
                className={tick === 0 ? "disc-norm-zero" : "disc-norm-grid"}
                x1={left}
                y1={y}
                x2={right}
                y2={y}
              />
              <text className="disc-norm-axis-label" x={right + 18} y={y + 4}>
                {tick}
              </text>
            </g>
          );
        })}

        <rect
          className="disc-norm-outline"
          x={left}
          y={top}
          width={right - left}
          height={bottom - top}
        />

        {DISC_TRAITS.map((trait) =>
          DISC_NORM_LEVELS.flatMap((level) => {
            const rawValues = getNormRawValues(normTable, trait, level);

            if (!rawValues.length) {
              return [];
            }

            const x = getX(trait);
            const y = getCellY(level);
            const { fontSize, rowHeight } = DISC_RAW_NUMBER_LAYOUT;
            const startY = y - ((rawValues.length - 1) * rowHeight) / 2;

            return rawValues.map((rawValue, index) => (
              <text
                key={`${trait}-${level}-${rawValue}-${index}`}
                className="disc-norm-raw-number"
                x={x}
                y={startY + index * rowHeight}
                fontSize={fontSize}
              >
                {rawValue}
              </text>
            ));
          })
        )}

        <polyline className="disc-norm-result-line" points={pointPath} />

        {points.map((point) => (
          <g key={`point-${point.trait}`} className="disc-norm-point">
            <circle cx={point.x} cy={point.y} r="9" />
            <text x={point.x} y={point.y + 3}>
              {point.rawValue}
            </text>
            <title>
              {point.trait}: raw {point.rawValue}, chart {point.chartValue}
            </title>
          </g>
        ))}

        {DISC_TRAITS.map((trait) => (
          <text
            key={`bottom-${trait}`}
            className="disc-norm-bottom-label"
            x={getX(trait)}
            y={bottom + 34}
          >
            {trait}
          </text>
        ))}
      </svg>
    </div>
  );
};

const DetailRes = () => {
  const { psychTestId, uid, resultId } = useParams();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [personalityData, setPersonalityData] = useState(null);
  const [isPersonalityLoading, setIsPersonalityLoading] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setPersonalityData(null);
        setIsPersonalityLoading(false);

        const response = await fetch(
          `https://psikotes-server-upj.vercel.app/results/${psychTestId}/${uid}/${resultId}`
        );
        if (!response.ok) {
          throw new Error(
            `Network response was not ok: ${response.statusText}`
          );
        }
        const data = await response.json();
        const resolvedPersonality =
          data.personality || determinePersonality(data.finalResults);

        setDetail({
          ...data,
          personality: resolvedPersonality,
        });

        // Fetch personality description
        if (!resolvedPersonality) {
          return;
        }

        setIsPersonalityLoading(true);

        try {
          const personalityResponse = await fetch(
            `https://psikotes-server-upj.vercel.app/personality/${resolvedPersonality}`
          );
          if (!personalityResponse.ok) {
            throw new Error(
              `Personality response was not ok: ${personalityResponse.statusText}`
            );
          }

          const personalityInfo = await personalityResponse.json();
          setPersonalityData(personalityInfo);
        } catch (personalityError) {
          console.error("Error fetching personality description:", personalityError);
        } finally {
          setIsPersonalityLoading(false);
        }
      } catch (error) {
        console.error("Error fetching detail:", error);
        setError(error.message);
      }
    };

    fetchDetail();
  }, [psychTestId, uid, resultId]);

  const discGraphs = useMemo(() => {
    if (!detail) {
      return { graph1: {}, graph2: {}, graph3: {} };
    }

    return calculateDiscGraphs(detail.categoryCounts, detail.finalResults);
  }, [detail]);

  const discRawScores = useMemo(() => {
    if (!detail) {
      return { most: {}, least: {}, change: {} };
    }

    return getDiscRawScores(detail.categoryCounts, detail.finalResults);
  }, [detail]);

  const discGraphDebugRows = useMemo(() => {
    if (!detail) {
      return [];
    }

    return buildDiscGraphDebugRows(
      detail.categoryCounts,
      detail.finalResults,
      discGraphs
    );
  }, [detail, discGraphs]);

  useEffect(() => {
    if (!discGraphDebugRows.length) {
      return;
    }

    console.groupCollapsed("DISC chart debug: raw score -> chart value");
    console.table(discGraphDebugRows);
    console.groupEnd();
  }, [discGraphDebugRows]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!detail) {
    return (
      <div className="d-flex justify-content-center align-items-center mt-3">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  const finalResults = detail.finalResults;
  const {
    graph1: graph1Values,
    graph2: graph2Values,
    graph3: graph3Values,
  } = discGraphs;
  const discLabels = {
    A: "D",
    B: "I",
    C: "S",
    D: "C",
    E: "E",
  };

  const orderedCategories = ["D", "I", "S", "C"];

  const validatedData = orderedCategories.map((cat) => {
    const value =
      finalResults[
        Object.keys(discLabels).find((key) => discLabels[key] === cat)
      ] || 0;
    return { category: cat, value: value };
  });

  const discNormGraphs = [
    {
      title: "Grafik MOST",
      subtitle: "PUBLIC",
      header: "MOST - Public",
      headerClassName: "disc-graph-header-most",
      normTable: DISC_NORM_TABLE.MOST,
      rawScores: discRawScores.most,
      chartValues: graph1Values,
      accentColor: "#1a6fa8",
    },
    {
      title: "Grafik LEAST",
      subtitle: "CORE",
      header: "LEAST - Core",
      headerClassName: "disc-graph-header-least",
      normTable: DISC_NORM_TABLE.LEAST,
      rawScores: discRawScores.least,
      chartValues: graph2Values,
      accentColor: "#b53050",
    },
    {
      title: "Grafik CHANGE",
      subtitle: "SELF",
      header: "CHANGE - Self",
      headerClassName: "disc-graph-header-change",
      normTable: DISC_NORM_TABLE.CHANGE,
      rawScores: discRawScores.change,
      chartValues: graph3Values,
      accentColor: "#2a8f8f",
    },
  ];

  const sortedData = validatedData.sort((a, b) => b.value - a.value);

  let personalityDetail = "";
  let hasNegative = false;

  sortedData.forEach((item) => {
    if (item.value >= 0) {
      personalityDetail += item.category;
    } else {
      if (!hasNegative) {
        personalityDetail += "/";
        hasNegative = true;
      }
      personalityDetail += item.category;
    }
  });

  const personalityColors = {
    D: "#fb6e6e",
    I: "#61af83",
    S: "#84cffc",
    C: "#fbe898",
  };

  const personalityColor = personalityColors[detail.personality] || "#000";

  return (
    <Row className="mt-3">
      <Col
        md={12}
        className="d-flex justify-content-between align-items-center"
      >
        <div>
          <h2>Detail Result Psikotes</h2>
        </div>
      </Col>
      <Col md={6} className="my-4">
        <Card className="card-detail-psi-admin">
          <Card.Header className="card-detail-header">User Detail</Card.Header>
          <Card.Body className="my-4">
            <div className="d-flex flex-column justify-content-center gap-1 mt-1 ">
              <p>
                <strong>Email:</strong> {detail.email}
              </p>
              <p>
                <strong>User Name:</strong> {detail.name}
              </p>
              <p>
                <strong>Status Tes:</strong> {detail.statusTes}
              </p>
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={6} className="my-4">
        <Card className="card-detail-psi-admin">
          <Card.Header className="card-detail-header">
            Test Completion Details
          </Card.Header>
          <Card.Body>
            <div className="d-flex flex-column justify-content-center gap-1 mt-2">
              <p>
                <strong>Completion Date:</strong> {detail.completionDate}
              </p>
              <p>
                <strong>Completion Time:</strong> {detail.completionTime}
              </p>
              <p>
                <strong>Dominant Personality:</strong> {detail.personality}
              </p>
              <p>
                <strong>Personality Details:</strong> {personalityDetail}
              </p>
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={12} className="my-4">
        <Card className="card-detail-psi-admin">
          <Card.Header className="card-detail-header">
            Personality Test Result
          </Card.Header>
          <Card.Body className="my-3">
            {personalityData ? (
              <div className="text-center">
                <h4 className="mb-3">
                  Kepribadian User:{" "}
                  <strong style={{ color: personalityColor }}>
                    {detail.personality} ({personalityData.type})
                  </strong>
                </h4>
                <p className="text-start" style={{ fontSize: "1rem", lineHeight: "1.6" }}>
                  {personalityData.description}
                </p>
              </div>
            ) : isPersonalityLoading ? (
              <div className="text-center">
                <Spinner animation="border" size="sm" role="status">
                  <span className="visually-hidden">Loading personality...</span>
                </Spinner>
                <p className="mt-2">Loading personality description...</p>
              </div>
            ) : (
              <div className="text-center">
                <h4 className="mb-3">
                  Kepribadian User:{" "}
                  <strong style={{ color: personalityColor }}>
                    {detail.personality || "-"}
                  </strong>
                </h4>
                <p className="mt-2 mb-0">
                  {detail.personality
                    ? "Deskripsi personality tidak tersedia untuk hasil ini."
                    : "Personality belum tersedia pada hasil ini."}
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
      <Col md={12} className="my-4">
        <Card className="card-detail-psi-admin">
          <Card.Header className="card-detail-header">
            Category Counts and Final Results
          </Card.Header>
          <Card.Body>
            <Table responsive bordered className="results-table">
              <thead>
                <tr>
                  <th></th>
                  <th>A</th>
                  <th>B</th>
                  <th>C</th>
                  <th>D</th>
                  <th>E</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-end fw-bold">Yes</td>
                  <td>{detail.categoryCounts.yes.A}</td>
                  <td>{detail.categoryCounts.yes.B}</td>
                  <td>{detail.categoryCounts.yes.C}</td>
                  <td>{detail.categoryCounts.yes.D}</td>
                  <td>{detail.categoryCounts.yes.E}</td>
                </tr>
                <tr>
                  <td className="text-end fw-bold">No</td>
                  <td>{detail.categoryCounts.no.A}</td>
                  <td>{detail.categoryCounts.no.B}</td>
                  <td>{detail.categoryCounts.no.C}</td>
                  <td>{detail.categoryCounts.no.D}</td>
                  <td>{detail.categoryCounts.no.E}</td>
                </tr>
                <tr>
                  <td className="text-end fw-bold">Final Result</td>
                  <td>{detail.finalResults.A}</td>
                  <td>{detail.finalResults.B}</td>
                  <td>{detail.finalResults.C}</td>
                  <td>{detail.finalResults.D}</td>
                  <td>{detail.finalResults.E}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th></th>
                  <th className="fw-bold">D</th>
                  <th className="fw-bold">I</th>
                  <th className="fw-bold">S</th>
                  <th className="fw-bold">C</th>
                  <th></th>
                </tr>
              </tfoot>
            </Table>
          </Card.Body>
        </Card>
      </Col>
      {/* DISC Psikogram: standard norm charts */}
      <Col md={12} className="my-2">
        <h5 className="disc-graph-section-title">DISC Psikogram</h5>
      </Col>
      {discNormGraphs.map((graph) => (
        <Col md={4} className="my-4" key={graph.header}>
          <Card className="card-detail-psi-admin disc-norm-card">
            <Card.Header
              className={`card-detail-header ${graph.headerClassName}`}
            >
              {graph.header}
            </Card.Header>
            <Card.Body className="disc-norm-card-body">
              <DiscNormGraph {...graph} />
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default DetailRes;
